/* eslint-disable @atlaskit/design-system/no-legacy-icons */
/* eslint-disable filenames/match-regex */
import { type DatabaseFieldIconType, FIELD_ICONS } from '@databases/icons/FieldTypeIcon';
import { getFieldTypeName } from '@databases/intl/field-type-names';
import { messages } from '@databases/intl/messages';
import { type DefaultRootState } from '@databases/redux';
import { type ObjectMode } from '@databases/types';
import {
  DatabaseFieldType,
  type ValueWithContext,
  type DatabaseField
} from '@databases/types/database';
import { getReferencedJiraIssueRefs } from '@databases/utils/database/field-types/jiraIssueDetails';
import { getReferencedLabelContentRefs } from '@databases/utils/database/field-types/pageLabels';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import React, { type ComponentType } from 'react';
import { type MessageDescriptor } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import AttachmentPopoverEdit from './edit/attachment-popover/AttachmentPopoverEdit.tsx';
import DateValueEdit from './edit/DateValueEdit.tsx';
import EntryLinkPopoverEdit from './edit/entry-link-popover/EntryLinkPopoverEdit.tsx';
import HyperlinkValuePopoverEdit from './edit/hyperlink-popover/HyperlinkPopoverEdit.tsx';
import JiraAssigneeValueEdit from './edit/jira-assignee/JiraAssigneeValueEdit.tsx';
import JiraIssueValuePopoverEdit from './edit/jira-issue-popover/JiraIssuePopoverEdit.tsx';
import JiraPriorityValueEdit from './edit/jira-priority/JiraPriorityValueEdit.tsx';
import JiraStatusValueEdit from './edit/jira-status/JiraStatusValueEdit.tsx';
import JiraSummaryValueEdit from './edit/jira-summary/JiraSummaryValueEdit.tsx';
import NumberValuePopoverEdit from './edit/NumberValuePopoverEdit.tsx';
import PageLabelsPopoverEdit from './edit/page-labels-popover/PageLabelsPopoverEdit.tsx';
import PageLinkValuePopoverEdit from './edit/page-link-popover/PageLinkPopoverEdit.tsx';
import PageStatusPopoverEdit from './edit/page-status-popover/PageStatusPopoverEdit.tsx';
import RichTextValuePopoverEdit from './edit/RichTextValuePopoverEditLazy.tsx';
import TagPopoverEdit from './edit/tag-popover/TagPopoverEdit.tsx';
import TextValuePopoverEdit from './edit/TextValuePopoverEdit.tsx';
import { type EditComponentProps } from './edit/types.ts';
import UserValuePopoverEdit from './edit/user-popover/UserPopoverEdit.tsx';
import { type TextLikeFieldType } from './types.ts';
import AttachmentValueView from './view/AttachmentValueView.tsx';
import BacklinkValueView from './view/BacklinkValueView.tsx';
import CheckboxValueView from './view/CheckboxValueView.tsx';
import ContentDetailsValueView from './view/ContentDetailsValueView.tsx';
import DateValueView from './view/DateValueView.tsx';
import EntryDetailsValueView from './view/EntryDetailsValueView.tsx';
import EntryLinkValueView from './view/EntryLinkValueView.tsx';
import HyperlinkValueView from './view/HyperlinkValueView.tsx';
import JiraIssueDetailsValueView from './view/JiraIssueDetailsValueView.tsx';
import JiraIssueValueView from './view/JiraIssueValueView.tsx';
import JiraSummaryValueView from './view/JiraSummaryValueView.tsx';
import NumberValueView from './view/NumberValueView.tsx';
import PageLinkValueView from './view/PageLinkValueView.tsx';
import RichTextValueView from './view/RichTextValueView.tsx';
import SelectValueView from './view/SelectValueView.tsx';
import { type ViewComponentProps } from './view/types.ts';
import UserValueView from './view/UserValueView.tsx';

function TextFieldView<Type extends TextLikeFieldType>(props: ViewComponentProps<Type>) {
  return <RichTextValueView {...props} />;
}

function TextFieldPopoverEdit<Type extends TextLikeFieldType>(
  props: EditComponentProps<Type>
) {
  return !props.isFilterValueEdit ? (
    <RichTextValuePopoverEdit {...props} />
  ) : (
    <TextValuePopoverEdit {...props} />
  );
}

const isAiSummaryEnabled = () =>
  getExperimentValue('databasesFrontendAiFieldsSummary', 'enabled', {
    fireExperimentExposure: false
  });

export enum FieldTypeCategory {
  BASIC = 'Basic',
  PAGE = 'Page',
  JIRA = 'Jira',
  OTHER_DATABASES = 'Other Databases'
}

export enum EditMode {
  /** Values of this type are not editable; the view component is always rendered. */
  READ_ONLY,

  /** Values of this type can switch between the view and edit component when it is focused/blurred. */
  INLINE_EDIT,

  /** The (seamless) edit component of this value should always be rendered (if the database is editable). */
  EDIT
}

export interface FieldType<Type extends DatabaseFieldType> extends DatabaseFieldIconType {
  label: MessageDescriptor;
  description?: MessageDescriptor;
  shortDescription?: MessageDescriptor;
  errorDescription?: MessageDescriptor;
  defaultText: MessageDescriptor;
  category: FieldTypeCategory;
  view: ComponentType<ViewComponentProps<Type>>;
  popoverEdit?: ComponentType<EditComponentProps<Type>>;
  edit?: ComponentType<EditComponentProps<Type>>;
  editMode: (
    context: ValueWithContext<Type, ObjectMode, Type>,
    state: DefaultRootState
  ) => EditMode;
}

type FieldTypes = { [Type in DatabaseFieldType]: FieldType<Type> };

let fieldTypesConfig: FieldTypes = buildFieldTypesConfig();

/**
 * Rebuilds field metadata that depends on the current feature flag state.
 */
export function rebuildFieldTypesConfig() {
  fieldTypesConfig = buildFieldTypesConfig();
}

export function getFieldTypes(): { [Type in DatabaseFieldType]: FieldType<Type> } {
  return fieldTypesConfig;
}

export function getFieldType<T extends DatabaseFieldType>(type: T): FieldType<T> {
  if (!Object.hasOwn(fieldTypesConfig, type)) {
    throw new Error(`Unknown field type ${type}.`);
  }

  return fieldTypesConfig[type];
}

function shouldUseJiraSummaryView(
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
  twoWayEnabled: boolean
): boolean {
  return field.settings.jiraField === 'summary' && twoWayEnabled;
}

function computeJiraIssueDetailsEditMode(
  context: ValueWithContext<
    DatabaseFieldType.JIRA_ISSUE_DETAILS,
    ObjectMode,
    DatabaseFieldType.JIRA_ISSUE_DETAILS
  >,
  state: DefaultRootState,
  twoWayEnabled: boolean
): EditMode {
  if (!twoWayEnabled) {
    return EditMode.READ_ONLY;
  }

  const field = context.field;

  // For summary fields, check if there are multiple issues and disable edit if so
  if (field.settings.jiraField === 'summary') {
    const refs = getReferencedJiraIssueRefs(context, state);
    if (refs.length > 1) {
      return EditMode.READ_ONLY;
    }
  }

  if (
    ['summary', 'status', 'priority', 'assignee'].includes(field.settings.jiraField ?? '')
  ) {
    return EditMode.INLINE_EDIT;
  }
  return EditMode.READ_ONLY;
}

function buildFieldTypesConfig(): FieldTypes {
  return {
    // Basic
    [DatabaseFieldType.TEXT]: {
      ...FIELD_ICONS[DatabaseFieldType.TEXT],
      label: getFieldTypeName(DatabaseFieldType.TEXT),
      shortDescription: messages['field-settings.type-text-short-description'],
      defaultText: messages['field.default-name.text'],
      category: FieldTypeCategory.BASIC,
      view: TextFieldView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: TextFieldPopoverEdit
    },
    [DatabaseFieldType.AI_SUMMARY]: {
      ...FIELD_ICONS[DatabaseFieldType.AI_SUMMARY],
      label: getFieldTypeName(DatabaseFieldType.AI_SUMMARY),
      shortDescription: messages['field-settings.type-ai-summary-short-description'],
      defaultText: messages['field.default-name.ai-summary'],
      category: FieldTypeCategory.BASIC,
      view: TextFieldView,
      popoverEdit: TextFieldPopoverEdit,
      editMode: () => (isAiSummaryEnabled() ? EditMode.INLINE_EDIT : EditMode.READ_ONLY)
    },
    [DatabaseFieldType.ATTACHMENT]: {
      ...FIELD_ICONS[DatabaseFieldType.ATTACHMENT],
      label: getFieldTypeName(DatabaseFieldType.ATTACHMENT),
      defaultText: messages['field.default-name.media'],
      category: FieldTypeCategory.BASIC,
      view: AttachmentValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: AttachmentPopoverEdit
    },
    [DatabaseFieldType.NUMBER]: {
      ...FIELD_ICONS[DatabaseFieldType.NUMBER],
      label: getFieldTypeName(DatabaseFieldType.NUMBER),
      defaultText: messages['field.default-name.number'],
      category: FieldTypeCategory.BASIC,
      view: NumberValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: NumberValuePopoverEdit
    },
    [DatabaseFieldType.DATE]: {
      ...FIELD_ICONS[DatabaseFieldType.DATE],
      label: getFieldTypeName(DatabaseFieldType.DATE),
      defaultText: messages['field.default-name.date'],
      category: FieldTypeCategory.BASIC,
      view: DateValueView,
      edit: DateValueEdit,
      editMode: () => EditMode.INLINE_EDIT
    },
    [DatabaseFieldType.SELECT]: {
      ...FIELD_ICONS[DatabaseFieldType.SELECT],
      label: getFieldTypeName(DatabaseFieldType.SELECT),
      shortDescription: messages['field-settings.type-tag-short-description'],
      defaultText: messages['field.default-name.tag'],
      category: FieldTypeCategory.BASIC,
      view: SelectValueView,
      popoverEdit: TagPopoverEdit,
      editMode: () => EditMode.INLINE_EDIT
    },
    [DatabaseFieldType.USER]: {
      ...FIELD_ICONS[DatabaseFieldType.USER],
      label: getFieldTypeName(DatabaseFieldType.USER),
      shortDescription: messages['field-settings.type-user-short-description'],
      defaultText: messages['field.default-name.user'],
      category: FieldTypeCategory.BASIC,
      view: UserValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: UserValuePopoverEdit
    },
    [DatabaseFieldType.HYPERLINK]: {
      ...FIELD_ICONS[DatabaseFieldType.HYPERLINK],
      label: getFieldTypeName(DatabaseFieldType.HYPERLINK),
      shortDescription: messages['field-settings.type-link-short-description'],
      defaultText: messages['field.default-name.hyperlink'],
      category: FieldTypeCategory.BASIC,
      view: HyperlinkValueView,
      popoverEdit: HyperlinkValuePopoverEdit,
      editMode: () => EditMode.INLINE_EDIT
    },
    [DatabaseFieldType.CHECKBOX]: {
      ...FIELD_ICONS[DatabaseFieldType.CHECKBOX],
      label: getFieldTypeName(DatabaseFieldType.CHECKBOX),
      shortDescription: messages['field-settings.type-checkbox-short-description'],
      defaultText: messages['field.default-name.checkbox'],
      category: FieldTypeCategory.BASIC,
      view: CheckboxValueView,
      edit: (props) => <CheckboxValueView {...props} recursionCount={0} />,
      editMode: () => EditMode.EDIT
    },
    [DatabaseFieldType.PAGE_LINK]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_LINK],
      label: getFieldTypeName(DatabaseFieldType.PAGE_LINK),
      shortDescription: messages['field-settings.type-content-link-short-description'],
      defaultText: messages['field.default-name.page-link'],
      category: FieldTypeCategory.PAGE,
      view: PageLinkValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: PageLinkValuePopoverEdit
    },
    [DatabaseFieldType.PAGE_STATUS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_STATUS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_STATUS),
      description: messages['field-settings.type-page-and-live-doc-status-description'],
      shortDescription: messages['field-settings.type-content-status-short-description'],
      errorDescription: messages['field-settings.type-status-error-description'],
      defaultText: messages['field.default-name.page-status'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      editMode: ({ database, field, entry }, state) =>
        !field.settings.allowEdit ||
        getReferencedStatusContentRefs({ database, field, entry }, state).length === 0
          ? EditMode.READ_ONLY
          : EditMode.INLINE_EDIT,
      popoverEdit: PageStatusPopoverEdit
    },
    [DatabaseFieldType.PAGE_LABELS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_LABELS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_LABELS),
      description: messages['field-settings.type-page-and-live-doc-label-description'],
      shortDescription: messages['field-settings.type-content-label-short-description'],
      errorDescription: messages['field-settings.type-label-error-description'],
      defaultText: messages['field.default-name.page-labels'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      editMode: ({ database, field, entry }, state) =>
        !field.settings.allowEdit ||
        getReferencedLabelContentRefs({ database, field, entry }, state).length === 0
          ? EditMode.READ_ONLY
          : EditMode.INLINE_EDIT,
      popoverEdit: PageLabelsPopoverEdit
    },
    [DatabaseFieldType.PAGE_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.PAGE_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.PAGE_DETAILS),
      description: messages['field-settings.type-page-and-live-doc-details-description'],
      shortDescription: messages['field-settings.type-content-details-short-description'],
      errorDescription: messages['field-settings.type-details-error-description'],
      defaultText: messages['field.default-name.page-details'],
      category: FieldTypeCategory.PAGE,
      view: (props) => <ContentDetailsValueView {...props} />,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY
    },
    [DatabaseFieldType.ENTRY_LINK]: {
      ...FIELD_ICONS[DatabaseFieldType.ENTRY_LINK],
      label: getFieldTypeName(DatabaseFieldType.ENTRY_LINK),
      description: messages['field-settings.type-entry-link-description'],
      shortDescription: messages['field-settings.type-entry-link-short-description'],
      defaultText: messages['field.default-name.entry-link'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: EntryLinkValueView,
      popoverEdit: EntryLinkPopoverEdit,
      editMode: () => EditMode.INLINE_EDIT
    },
    [DatabaseFieldType.BACKLINK]: {
      ...FIELD_ICONS[DatabaseFieldType.BACKLINK],
      label: getFieldTypeName(DatabaseFieldType.BACKLINK),
      description: messages['field-settings.type-entry-backlink-description'],
      shortDescription: messages['field-settings.type-entry-backlink-short-description'],
      defaultText: messages['field.default-name.backlink'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: BacklinkValueView,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY
    },
    [DatabaseFieldType.ENTRY_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.ENTRY_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.ENTRY_DETAILS),
      description: messages['field-settings.type-entry-details-description'],
      shortDescription: messages['field-settings.type-entry-details-short-description'],
      errorDescription: messages['field-settings.type-entry-details-error-description'],
      defaultText: messages['field.default-name.entry-details'],
      category: FieldTypeCategory.OTHER_DATABASES,
      view: EntryDetailsValueView,
      edit: () => null,
      editMode: () => EditMode.READ_ONLY
    },
    [DatabaseFieldType.JIRA_ISSUE]: {
      ...FIELD_ICONS[DatabaseFieldType.JIRA_ISSUE],
      label: getFieldTypeName(DatabaseFieldType.JIRA_ISSUE),
      shortDescription: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-short-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-short-description'],
      defaultText: messages['field.default-name.jira-issue'],
      category: FieldTypeCategory.JIRA,
      view: JiraIssueValueView,
      editMode: () => EditMode.INLINE_EDIT,
      popoverEdit: JiraIssueValuePopoverEdit
    },
    [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
      ...FIELD_ICONS[DatabaseFieldType.JIRA_ISSUE_DETAILS],
      label: getFieldTypeName(DatabaseFieldType.JIRA_ISSUE_DETAILS),
      description: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-details-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-details-description'],
      shortDescription: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-details-short-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-details-short-description'],
      errorDescription: isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
        ? messages[
            'field-settings.type-jira-issue-details-error-description-issue-terminology-refresh-m4'
          ]
        : messages['field-settings.type-jira-issue-details-error-description'],
      defaultText: messages['field.default-name.jira-issue-details'],
      category: FieldTypeCategory.JIRA,
      view: (props: ViewComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>) => {
        const twoWayEnabled = Boolean(
          getExperimentValue('databases2waySyncEnabled', 'enabled', {
            fireExperimentExposure: true
          })
        );
        return shouldUseJiraSummaryView(
          props.field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
          twoWayEnabled
        ) ? (
          <JiraSummaryValueView {...props} />
        ) : (
          <JiraIssueDetailsValueView {...props} />
        );
      },
      popoverEdit: (props: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>) =>
        props.field.settings.jiraField === 'status' ? (
          <JiraStatusValueEdit {...(props as any)} />
        ) : props.field.settings.jiraField === 'summary' ? (
          <JiraSummaryValueEdit {...props} />
        ) : props.field.settings.jiraField === 'priority' ? (
          <JiraPriorityValueEdit {...props} />
        ) : props.field.settings.jiraField === 'assignee' ? (
          <JiraAssigneeValueEdit {...props} />
        ) : undefined,
      editMode: (context, state) => {
        const twoWayEnabled = Boolean(
          getExperimentValue('databases2waySyncEnabled', 'enabled', {
            fireExperimentExposure: true
          })
        );
        return computeJiraIssueDetailsEditMode(context, state, twoWayEnabled);
      }
    }
  };
}
